'use client';

import { useCallback, useState } from 'react';

import { getChaptersContentByWorkIdReq } from '@/features/fanfic-page/requests/chapters.requests';
import type { Chapter } from '@/features/fanfic-page/types/fanfic';

export interface UseChaptersContentQueryReturn {
  chapters: Chapter[];
  isLoading: boolean;
  error: Error | null;
  totalDocs: number;
  totalPages: number;
  currentPage: number;
  hasMore: boolean;
  fetchChapters: (page?: number) => Promise<void>;
  loadMore: () => Promise<void>;
  loadRange: (startPage: number, endPage: number) => Promise<void>;
  reset: () => void;
}

export interface UseChaptersContentQueryProps {
  workId: string;
  limit?: number;
  initialChapters?: Chapter[];
  initialPage?: number;
}

export function useChaptersContentQuery({
  workId,
  limit = 5,
  initialChapters = [],
  initialPage = 0,
}: UseChaptersContentQueryProps): UseChaptersContentQueryReturn {
  const [chapters, setChapters] = useState<Chapter[]>(initialChapters);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const [totalDocs, setTotalDocs] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(initialPage);

  const fetchChapters = useCallback(
    async (page: number = 1) => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await getChaptersContentByWorkIdReq({
          workId,
          page,
          limit,
        });

        setChapters((prev) => {
          // Create a map of existing chapters by id
          const existingMap = new Map(prev.map((ch) => [ch.id, ch]));

          // Add new chapters from response
          response.chapters.forEach((ch) => {
            existingMap.set(ch.id, ch);
          });

          // Convert back to array and sort by order
          return Array.from(existingMap.values()).sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        });

        setTotalDocs(response.totalDocs);
        setTotalPages(response.totalPages);
        setCurrentPage(page);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to fetch chapters'));
        console.error('Failed to fetch chapters:', err);
      } finally {
        setIsLoading(false);
      }
    },
    [workId, limit],
  );

  const loadMore = useCallback(async () => {
    if (currentPage < totalPages && !isLoading) {
      await fetchChapters(currentPage + 1);
    }
  }, [currentPage, totalPages, isLoading, fetchChapters]);

  const loadRange = useCallback(
    async (startPage: number, endPage: number) => {
      if (isLoading) return;

      setIsLoading(true);
      setError(null);

      try {
        const pagesToLoad = [];
        for (let page = startPage; page <= endPage && page <= totalPages; page++) {
          pagesToLoad.push(page);
        }

        const responses = await Promise.all(
          pagesToLoad.map((page) =>
            getChaptersContentByWorkIdReq({
              workId,
              page,
              limit,
            }),
          ),
        );

        // Merge all chapters from responses
        const allNewChapters = responses.flatMap((res) => res.chapters);

        setChapters((prev) => {
          // Create a map of existing chapters by id
          const existingMap = new Map(prev.map((ch) => [ch.id, ch]));

          // Add new chapters to map
          allNewChapters.forEach((ch) => {
            existingMap.set(ch.id, ch);
          });

          // Convert back to array and sort by order
          return Array.from(existingMap.values()).sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        });

        if (responses.length > 0) {
          const lastResponse = responses[responses.length - 1];
          setTotalDocs(lastResponse.totalDocs);
          setTotalPages(lastResponse.totalPages);
          setCurrentPage(Math.max(...pagesToLoad));
        }
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to load chapter range'));
        console.error('Failed to load chapter range:', err);
      } finally {
        setIsLoading(false);
      }
    },
    [workId, limit, totalPages, isLoading],
  );

  const reset = useCallback(() => {
    setChapters([]);
    setCurrentPage(0);
    setTotalDocs(0);
    setTotalPages(0);
    setError(null);
  }, []);

  const hasMore = currentPage < totalPages;

  return {
    chapters,
    isLoading,
    error,
    totalDocs,
    totalPages,
    currentPage,
    hasMore,
    fetchChapters,
    loadMore,
    loadRange,
    reset,
  };
}
